import { useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import Button from '../common/Button';
import FormInput from '../components/form/FormInput';
import PasswordChangeSentDialog from '../components/PasswordChangeSentDialog';
import { useResetPasswordRequest } from '../hooks/tanstack/useAuthHook';
import { handleQueryError } from '../utils/api';
import cn from '../utils/cn';

const PasswordChangeFormFormSchema = z.object({
  email: z.string().trim().email('Enter your email address'),
});

type PasswordChangeFormSchemaType = z.infer<
  typeof PasswordChangeFormFormSchema
>;

const PasswordChangeForm = () => {
  const [error, setError] = useState(Object);
  const [searchParams, setSearchParams] = useSearchParams();

  const [openChangePasswordSentDialog, setOpenChangePasswordSentDialog] =
    useState(false);

  const { mutate: changePassword, isPending } = useResetPasswordRequest({
    onSuccess: () => {
      // @todo: Need to handle this
      setOpenChangePasswordSentDialog(true);
    },
    onError: (error) => {
      handleQueryError(error, setError);
    },
  });

  const form = useForm<PasswordChangeFormSchemaType>({
    resolver: zodResolver(PasswordChangeFormFormSchema),
  });

  const { handleSubmit } = form;

  const onSubmit: SubmitHandler<PasswordChangeFormSchemaType> = (data) => {
    changePassword({ data });
  };

  const handleCTAOnClick = () => {
    searchParams.delete('type');
    setSearchParams(searchParams);
  };

  const currentYear = new Date().getFullYear();

  const type = searchParams.get('type');

  return (
    <div
      className={cn(
        'absolute flex w-full translate-y-[120vh] justify-center transition duration-500',
        {
          'translate-y-0': type === 'reset_password',
        }
      )}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="border-grey-500 text-white flex w-full max-w-[600px] flex-col items-start gap-3 rounded-xl border-[2px] px-8 py-6 backdrop-blur-sm"
        >
          <div className="flex flex-col pb-6">
            <h1 className="text-2xl font-bold uppercase">
              forgot your password
            </h1>
            <p className="text-sm">Your password will be reset by email.</p>
          </div>
          <FormInput
            fullWidth
            name="email"
            label="Email"
            labelClassName="text-white"
            style={{
              backgroundColor: 'white',
            }}
          />
          <Button
            loading={isPending}
            type="submit"
            variant="contained_red"
            className="w-full"
          >
            Submit
          </Button>
          <button
            type="button"
            onClick={handleCTAOnClick}
            className="w-full py-5 text-center"
          >
            <p>Back to Login</p>
          </button>
          <div className="w-full text-center font-light">
            <p>@{currentYear} ISBE Group</p>
          </div>
          <PasswordChangeSentDialog
            open={openChangePasswordSentDialog}
            handleOnClose={() => {
              setOpenChangePasswordSentDialog(false);
            }}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default PasswordChangeForm;
